import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthUser, AuthResponse, Warehouse } from '../models/auth.models';
import { checkLiferayStatus } from '../utils/liferay-response';

const SESSION_KEY = 'hp_pos_session';

interface StoredSession {
  user: AuthUser;
  token: string;
  warehouseId: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private _currentUser = signal<AuthUser | null>(this.loadUser());
  private _token = signal<string | null>(this.loadToken());
  private _warehouseId = signal<string>(this.loadWarehouseId());

  readonly currentUser = this._currentUser.asReadonly();
  readonly isAuthenticated = computed(() => this._currentUser() !== null);

  /** All warehouses assigned to the current user. */
  readonly warehouses = computed<Warehouse[]>(() => this._currentUser()?.warehouses ?? []);
  readonly selectedWarehouseId = this._warehouseId.asReadonly();
  readonly selectedWarehouseName = computed(
    () => this.warehouses().find(w => w.warehouseId === this._warehouseId())?.warehouseName ?? ''
  );

  login(username: string, password: string) {
    const url = `${environment.apiUrl}/api/jsonws/th.patient/authenticate-user`;
    const body = new HttpParams().set('username', username).set('password', password);

    return this.http
      .post<AuthResponse>(url, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      })
      .pipe(
        checkLiferayStatus(),
        map((response) => {
          if (response.authStatus !== 1) {
            throw new Error('Invalid credentials. Please try again.');
          }
          if (!response.hasWarehouse) {
            throw new Error(
              'No warehouse assigned to your account. Please contact your administrator.'
            );
          }
          return response;
        }),
        tap((response) => {
          const { token, ...user } = response;
          const warehouseId = user.warehouses?.[0]?.warehouseId ?? '';
          this._currentUser.set(user as AuthUser);
          this._token.set(token);
          this._warehouseId.set(warehouseId);
          this.persistSession(user as AuthUser, token, warehouseId);
        })
      );
  }

  logout(): void {
    this._currentUser.set(null);
    this._token.set(null);
    this._warehouseId.set('');
    localStorage.removeItem(SESSION_KEY);
    this.router.navigate(['/login']);
  }

  /** Used internally by the auth interceptor only */
  getToken(): string | null {
    return this._token();
  }

  /** Switches the active warehouse in place and reloads so every page refetches with it. */
  setWarehouse(warehouseId: string): void {
    if (!warehouseId || warehouseId === this._warehouseId()) return;
    this._warehouseId.set(warehouseId);
    this.updateSession({ warehouseId });
    window.location.reload();
  }

  private loadUser(): AuthUser | null {
    return this.loadSession()?.user ?? null;
  }

  private loadToken(): string | null {
    return this.loadSession()?.token ?? null;
  }

  private loadWarehouseId(): string {
    return this.loadSession()?.warehouseId ?? '';
  }

  private loadSession(): StoredSession | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      return raw ? (JSON.parse(raw) as StoredSession) : null;
    } catch {
      return null;
    }
  }

  private persistSession(user: AuthUser, token: string, warehouseId: string): void {
    const session: StoredSession = { user, token, warehouseId };
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }

  private updateSession(patch: Partial<Pick<StoredSession, 'warehouseId'>>): void {
    const session = this.loadSession();
    if (!session) return;
    localStorage.setItem(SESSION_KEY, JSON.stringify({ ...session, ...patch }));
  }
}
